import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ZplPrinterListStatus } from "@/components/ZplPrinterSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import {
  fetchMiscCupsPrinters,
  fetchPddZplCatch,
  fetchPddZplCatchPreview,
  formatCupsPrinterOptionLabel,
  MISC_CUPS_PRINTERS_QUERY_KEY,
  MISC_PRINT_LAST_PRINTER_STORAGE_KEY,
  miscPrintFileAllowed,
  PDD_ZPL_CATCH_QUERY_KEY,
  pddZplCatchKindLabel,
  postMiscCupsPrint,
  postPddZplCatchPrint,
  type PddZplCatchJob,
} from "@/lib/miscPrintingApi";
import { cn } from "@/lib/utils";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";

const PAGE_HELP =
  "Print an office document on a shared CUPS printer, or open PDD labels to preview and print ABB catch files to a 203 DPI Zebra. Assign carton and box labels to a batch line from Batch details when you have manufacturing write access. The hub strips the printer memory-wipe command so office printers keep their fonts.";

type PrintPageTab = "document" | "pdd";
type PddCatchSortKey = "receivedAt" | "kind" | "format" | "printCopies";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PersonalPrintingPage() {
  const [activeTab, setActiveTab] = useState<PrintPageTab>("document");

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Print</PageTitleWithHelp>
      </header>

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 pb-px"
        role="tablist"
        aria-label="Print sections"
      >
        <button
          type="button"
          role="tab"
          id="print-tab-document"
          aria-selected={activeTab === "document"}
          aria-controls="print-panel-document"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            activeTab === "document"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setActiveTab("document")}
        >
          Document print
        </button>
        <button
          type="button"
          role="tab"
          id="print-tab-pdd"
          aria-selected={activeTab === "pdd"}
          aria-controls="print-panel-pdd"
          className={cn(
            "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
            activeTab === "pdd"
              ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
              : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
          )}
          onClick={() => setActiveTab("pdd")}
        >
          PDD labels
        </button>
      </div>

      {activeTab === "document" ? (
        <div role="tabpanel" id="print-panel-document" aria-labelledby="print-tab-document">
          <DocumentPrintCard />
        </div>
      ) : (
        <div role="tabpanel" id="print-panel-pdd" aria-labelledby="print-tab-pdd">
          <PddZplCatchPrintCard />
        </div>
      )}
    </div>
  );
}

function DocumentPrintCard(): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [printer, setPrinter] = useState("");
  const [copies, setCopies] = useState("1");
  const [pages, setPages] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const printersQuery = useQuery({
    queryKey: MISC_CUPS_PRINTERS_QUERY_KEY,
    queryFn: fetchMiscCupsPrinters,
  });

  const printers = printersQuery.data?.printers ?? [];

  useEffect(() => {
    if (printer !== "" || printers.length === 0) return;
    const saved = localStorage.getItem(MISC_PRINT_LAST_PRINTER_STORAGE_KEY);
    if (saved && printers.some((row) => row.name === saved)) {
      setPrinter(saved);
      return;
    }
    if (printers.length === 1) {
      setPrinter(printers[0]!.name);
    }
  }, [printer, printers]);

  const printMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error("Choose a file to print.");
      if (!printer.trim()) throw new Error("Choose a printer.");
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("printer", printer.trim());
      const copyCount = Number.parseInt(copies, 10);
      if (Number.isFinite(copyCount) && copyCount > 1) {
        formData.append("copies", String(copyCount));
      }
      const pageRanges = pages.trim();
      if (pageRanges) {
        formData.append("page-ranges", pageRanges);
      }
      return postMiscCupsPrint(formData);
    },
    onSuccess: (data) => {
      localStorage.setItem(MISC_PRINT_LAST_PRINTER_STORAGE_KEY, data.printer);
      setMessage({
        type: "success",
        text: `Sent to ${data.printer}${data.copies > 1 ? ` (${data.copies} copies)` : ""}.`,
      });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setCopies("1");
      setPages("");
    },
    onError: (error) => {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Print failed.",
      });
    },
  });

  const canPrint = Boolean(selectedFile && printer.trim()) && !printMutation.isPending;

  const printerLoadError = useMemo(() => {
    if (printersQuery.isLoading) return null;
    if (printersQuery.error instanceof Error) return printersQuery.error.message;
    if (!printersQuery.isError && printers.length === 0) {
      return "No CUPS printers were returned from the print relay.";
    }
    return null;
  }, [printers.length, printersQuery.error, printersQuery.isError, printersQuery.isLoading]);

  function handleFileSelect(file: File | null) {
    setMessage(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!miscPrintFileAllowed(file, "document")) {
      setMessage({ type: "error", text: "Unsupported file type for office printing." });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitleWithHelp hoverHint="Upload a PDF or other supported document, choose a CUPS queue, and send the job through the hub print relay.">
          Document print
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="misc-print-file">File</Label>
          {!selectedFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-sm text-slate-600 transition hover:border-brand-400 hover:bg-brand-50/40",
              )}
            >
              <Upload className="h-8 w-8 text-slate-400" aria-hidden />
              <span>Tap to select a file or drag and drop</span>
              <span className="text-xs text-slate-500">PDF, PNG, JPEG, GIF, text, PostScript</span>
            </button>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleFileSelect(null)}
              >
                Clear
              </Button>
            </div>
          )}
          <input
            ref={fileInputRef}
            id="misc-print-file"
            type="file"
            className="sr-only"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.txt,.ps,.eps,application/pdf,image/*,text/plain"
            onChange={(event) => handleFileSelect(event.target.files?.[0] ?? null)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="misc-print-printer">Printer</Label>
            <select
              id="misc-print-printer"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              value={printer}
              disabled={printersQuery.isLoading || Boolean(printerLoadError)}
              onChange={(event) => setPrinter(event.target.value)}
            >
              <option value="">Select printer…</option>
              {printers.map((row) => (
                <option key={row.name} value={row.name}>
                  {formatCupsPrinterOptionLabel(row)}
                </option>
              ))}
            </select>
            {printersQuery.isLoading ? (
              <p className="text-xs text-slate-500">Loading printers…</p>
            ) : null}
            {printerLoadError ? (
              <p className="text-xs text-amber-800">{printerLoadError}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="misc-print-copies">Copies</Label>
            <Input
              id="misc-print-copies"
              type="number"
              min={1}
              step={1}
              value={copies}
              onChange={(event) => setCopies(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="misc-print-pages">Pages</Label>
            <Input
              id="misc-print-pages"
              placeholder="All"
              value={pages}
              onChange={(event) => setPages(event.target.value)}
            />
          </div>
        </div>

        {message ? (
          <p
            className={cn(
              "rounded-md px-3 py-2 text-sm",
              message.type === "success" ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900",
            )}
          >
            {message.text}
          </p>
        ) : null}

        <Button
          type="button"
          className="w-full sm:w-auto"
          disabled={!canPrint}
          onClick={() => {
            setMessage(null);
            printMutation.mutate();
          }}
        >
          {printMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            "Print"
          )}
        </Button>
      </CardBody>
    </Card>
  );
}

function comparePddCatchJobs(
  left: PddZplCatchJob,
  right: PddZplCatchJob,
  sortKey: PddCatchSortKey,
  dir: SortDirection,
): number {
  const direction = dir === "asc" ? 1 : -1;
  if (sortKey === "receivedAt") {
    return (Date.parse(left.receivedAt) - Date.parse(right.receivedAt)) * direction;
  }
  if (sortKey === "printCopies") {
    return (left.printCopies - right.printCopies) * direction;
  }
  const leftText = sortKey === "kind" ? left.kind : left.format;
  const rightText = sortKey === "kind" ? right.kind : right.format;
  return leftText.localeCompare(rightText) * direction;
}

function formatCatchReceivedAt(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toLocaleString();
}

function PddZplCatchPrintCard(): JSX.Element {
  const { station, setStation, selectedPrinter, unavailableMessage, printersQuery } = useZplPrintLocation({
    labelSize: "4x6",
    dpi: 203,
    storageKey: ZPL_PRINTER_STORAGE_KEYS.personalPddCatch,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<PddCatchSortKey>("receivedAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const catchQuery = useQuery({
    queryKey: PDD_ZPL_CATCH_QUERY_KEY,
    queryFn: fetchPddZplCatch,
    refetchInterval: 15_000,
  });

  const jobs = catchQuery.data?.jobs ?? [];
  const sortedJobs = useMemo(
    () => [...jobs].sort((left, right) => comparePddCatchJobs(left, right, sortKey, sortDir)),
    [jobs, sortDir, sortKey],
  );
  const selectedJob = selectedId != null ? (jobs.find((job) => job.id === selectedId) ?? null) : null;

  const previewQuery = useQuery({
    queryKey: [...PDD_ZPL_CATCH_QUERY_KEY, "preview", selectedId],
    queryFn: () => fetchPddZplCatchPreview(selectedId ?? ""),
    enabled: selectedId != null,
    staleTime: 10 * 60_000,
  });

  const printMutation = useMutation({
    mutationFn: async () => {
      if (selectedId == null) throw new Error("Select a PDD label job.");
      if (selectedPrinter.trim() === "") throw new Error("Choose a Zebra printer.");
      return postPddZplCatchPrint({ ids: [selectedId], printer: selectedPrinter.trim() });
    },
    onSuccess: (data) => {
      const kinds = data.printed.map((row) => pddZplCatchKindLabel(row.kind).toLowerCase());
      setMessage({
        type: "success",
        text: `Sent ${String(data.printed.length)} job${data.printed.length === 1 ? "" : "s"} (${kinds.join(", ")}) to ${data.printer}.`,
      });
    },
    onError: (error) => {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Print failed.",
      });
    },
  });

  function toggleSort(nextKey: PddCatchSortKey): void {
    if (nextKey === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(nextKey === "receivedAt" ? "desc" : "asc");
  }

  const canPrint = selectedId != null && selectedPrinter.trim() !== "" && !printMutation.isPending;
  const emptyReason = !catchQuery.data?.available
    ? "The PDD catch folder is not available on this hub host."
    : "No PDD labels are in the catch folder.";

  return (
    <HubViewportCard>
      <CardHeader className="pb-2">
        <CardTitleWithHelp hoverHint="ABB PDD prints land here after FTP to the fake Zebra on GoDaddy. Click a row to preview, then print that job to a 203 DPI Zebra. Assign carton and box labels to a batch line from Batch details. The hub strips the printer memory-wipe command so office printers keep their fonts.">
          PDD labels
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="flex min-h-0 flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <ZplLocationSelect
            id="pdd-zpl-catch-location"
            value={station}
            onChange={setStation}
            unavailableMessage={unavailableMessage}
            hoverHint="PDD labels are 203 DPI 4x6. Hub uses the Shipping printer that matches, or shows that location as unavailable."
          />
          <div className="flex items-end">
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={!canPrint}
              onClick={() => {
                setMessage(null);
                printMutation.mutate();
              }}
            >
              {printMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Sending…
                </>
              ) : (
                "Print"
              )}
            </Button>
          </div>
        </div>
        <ZplPrinterListStatus printersQuery={printersQuery} />
        {catchQuery.isLoading ? <p className="text-sm text-slate-500">Loading catch folder…</p> : null}
        {catchQuery.isError ? (
          <p className="text-sm text-amber-800">
            {catchQuery.error instanceof Error ? catchQuery.error.message : "Could not load PDD catch jobs."}
          </p>
        ) : null}
        {message ? (
          <p
            className={cn(
              "rounded-md px-3 py-2 text-sm",
              message.type === "success" ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900",
            )}
          >
            {message.text}
          </p>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
          <div className="min-h-0 min-w-0 flex-1 overflow-auto">
            <table className="w-full min-w-[40rem] border-collapse text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                <tr>
                  <SortableTableHeader
                    label="Received"
                    sortKey="receivedAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    label="Kind"
                    sortKey="kind"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    label="Format"
                    sortKey="format"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    label="Print copies"
                    sortKey="printCopies"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <th
                    scope="col"
                    className="bg-slate-50 px-2 py-2 text-right text-xs font-medium uppercase tracking-wide text-slate-600 tabular-nums"
                  >
                    Label qty
                  </th>
                  <th
                    scope="col"
                    className="bg-slate-50 px-2 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-600"
                  >
                    Catalog
                  </th>
                  <th
                    scope="col"
                    className="bg-slate-50 px-2 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-600"
                  >
                    File
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.length === 0 && !catchQuery.isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-6 text-center text-slate-500">
                      {emptyReason}
                    </td>
                  </tr>
                ) : (
                  sortedJobs.map((job) => (
                    <tr
                      key={job.id}
                      className={cn(
                        "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                        selectedId === job.id ? "bg-sky-50 hover:bg-sky-50" : null,
                      )}
                      onClick={() => {
                        setMessage(null);
                        setSelectedId(job.id);
                      }}
                    >
                      <td className="whitespace-nowrap px-2 py-2 text-slate-700">
                        {formatCatchReceivedAt(job.receivedAt)}
                      </td>
                      <td className="px-2 py-2 text-slate-900">{pddZplCatchKindLabel(job.kind)}</td>
                      <td className="px-2 py-2 text-slate-700">{job.format}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-900">{job.printCopies}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                        {job.labelQty ?? "—"}
                      </td>
                      <td className="px-2 py-2 text-slate-700">
                        <div>{job.catalog ?? "—"}</div>
                        {job.sku ? <div className="text-xs text-slate-500">{job.sku}</div> : null}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500">{job.originalName}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <aside className="w-full shrink-0 lg:w-96">
            <figure className="space-y-2">
              <div className="flex h-[min(36rem,70vh)] items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                {selectedJob == null ? (
                  <p className="px-3 text-center text-xs text-slate-500">Click a row to preview.</p>
                ) : previewQuery.isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-hidden />
                ) : previewQuery.isError ? (
                  <p className="px-2 text-center text-xs text-amber-800">
                    {previewQuery.error instanceof Error ? previewQuery.error.message : "Preview failed."}
                  </p>
                ) : previewQuery.data?.pngBase64 ? (
                  <img
                    src={`data:image/png;base64,${previewQuery.data.pngBase64}`}
                    alt={`${pddZplCatchKindLabel(selectedJob.kind)} label preview`}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <p className="text-xs text-slate-500">No preview.</p>
                )}
              </div>
              <figcaption className="min-h-[4.5rem] text-xs text-slate-600">
                {selectedJob == null ? (
                  <p className="text-slate-500">Preview stays here while you pick a label to print.</p>
                ) : (
                  <>
                    <div className="font-medium text-slate-900">{pddZplCatchKindLabel(selectedJob.kind)}</div>
                    <div>{selectedJob.catalog ?? selectedJob.originalName}</div>
                    {selectedJob.sku ? <div className="text-slate-500">{selectedJob.sku}</div> : null}
                    <div className="text-slate-500">{formatCatchReceivedAt(selectedJob.receivedAt)}</div>
                  </>
                )}
              </figcaption>
            </figure>
          </aside>
        </div>
      </CardBody>
    </HubViewportCard>
  );
}
